import type { LocalizedText } from "@packages/core/language/language.ts";
import type { MemberFilters } from "#shared/reviewee-filters/memberFilters.ts";

export const TENURE_CUTOFFS = [12, 36, 72, 120];

export const TENURE_BUCKET_KEYS = ["0", "12", "36", "72", "120"] as const;
export type TenureBucketKey = (typeof TENURE_BUCKET_KEYS)[number];

export const TENURE_BAND_LABELS: Record<TenureBucketKey, LocalizedText> = {
    "0": { "en-US": "Under 1 year", "fr-CA": "Moins d'un an" },
    "12": { "en-US": "1–2 years", "fr-CA": "1 à 2 ans" },
    "36": { "en-US": "3–5 years", "fr-CA": "3 à 5 ans" },
    "72": { "en-US": "6–9 years", "fr-CA": "6 à 9 ans" },
    "120": { "en-US": "10+ years", "fr-CA": "10 ans et plus" }
};

export function toTenureMonthRange(groupKey: string): { lowerMonths: number; upperMonths: number | undefined } | undefined {
    if (!groupKey.trim()) {
        return undefined;
    }

    const lowerMonths = Number(groupKey);

    if (!Number.isFinite(lowerMonths) || lowerMonths < 0) {
        return undefined;
    }

    return { lowerMonths, upperMonths: TENURE_CUTOFFS.find(cutoff => cutoff > lowerMonths) };
}

function shiftMonthsToDateOnly(from: Date, months: number): string {
    const shifted = new Date(from);
    shifted.setUTCMonth(shifted.getUTCMonth() - months);

    return shifted.toISOString().slice(0, 10);
}

export function toTenureFilters(groupKey: string, now: Date): MemberFilters | undefined {
    const range = toTenureMonthRange(groupKey);

    if (!range) {
        return undefined;
    }

    return {
        hireDateMin: range.upperMonths === undefined ? undefined : shiftMonthsToDateOnly(now, range.upperMonths),
        hireDateMax: shiftMonthsToDateOnly(now, range.lowerMonths)
    };
}

export function toTenureBucketKey(filters: Pick<MemberFilters, "hireDateMin" | "hireDateMax">, now: Date): TenureBucketKey | undefined {
    return TENURE_BUCKET_KEYS.find(key => {
        const band = toTenureFilters(key, now);

        return band?.hireDateMin === filters.hireDateMin && band?.hireDateMax === filters.hireDateMax;
    });
}
